import React, { useEffect, useRef } from 'react';
import { getLnatHighlightFill } from '../constants/lnatPearsonHighlightTokens';
import { offsetsToRange, unwrapLnatMarks } from '../utils/lnatPearsonTextOffsets';

function wrapTextNodeSlice(textNode, localStart, localEnd, className, style) {
  if (!textNode || !textNode.parentNode || localStart >= localEnd) return;
  const text = textNode.nodeValue || '';
  const parent = textNode.parentNode;
  if (
    localStart === 0
    && localEnd === text.length
    && parent.nodeType === 1
    && parent.childNodes.length === 1
    && parent.classList
    && (parent.classList.contains('lnat-pearson-hl') || parent.classList.contains('lnat-pearson-strike'))
  ) {
    className.split(/\s+/).filter(Boolean).forEach((c) => parent.classList.add(c));
    if (style && style.backgroundColor && className.indexOf('lnat-pearson-hl') !== -1) {
      parent.style.backgroundColor = style.backgroundColor;
    }
    return;
  }
  const span = document.createElement('span');
  span.className = className;
  if (style && style.backgroundColor) span.style.backgroundColor = style.backgroundColor;
  if (style && style.textDecorationColor) {
    span.style.textDecorationColor = style.textDecorationColor;
  }
  span.appendChild(document.createTextNode(text.slice(localStart, localEnd)));
  const before = text.slice(0, localStart);
  const after = text.slice(localEnd);
  if (before) parent.insertBefore(document.createTextNode(before), textNode);
  parent.insertBefore(span, textNode);
  if (after) parent.insertBefore(document.createTextNode(after), textNode);
  parent.removeChild(textNode);
}

function wrapOffsetRange(root, start, end, className, style) {
  const range = offsetsToRange(root, start, end);
  if (!range) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const pieces = [];
  let pos = 0;
  let node = walker.nextNode();
  while (node) {
    const len = node.nodeValue ? node.nodeValue.length : 0;
    const overlapStart = Math.max(start, pos);
    const overlapEnd = Math.min(end, pos + len);
    if (overlapStart < overlapEnd) {
      pieces.push({
        node,
        localStart: overlapStart - pos,
        localEnd: overlapEnd - pos,
      });
    }
    pos += len;
    if (pos >= end) break;
    node = walker.nextNode();
  }
  for (let i = pieces.length - 1; i >= 0; i -= 1) {
    wrapTextNodeSlice(pieces[i].node, pieces[i].localStart, pieces[i].localEnd, className, style);
  }
}

function paneMatches(range, pane, optionLabel) {
  if (!range || range.pane !== pane) return false;
  if (pane === 'option') return range.optionLabel === optionLabel;
  return true;
}

export default function LnatPearsonAnnotatable({
  pane,
  optionLabel,
  highlights = [],
  strikes = [],
  colorScheme,
  mathJaxEpoch = 0,
  questionId,
  className,
  children,
}) {
  const rootRef = useRef(null);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    unwrapLnatMarks(root);
    (highlights || []).forEach((h) => {
      if (!paneMatches(h, pane, optionLabel)) return;
      const fill = getLnatHighlightFill(h.color, colorScheme);
      wrapOffsetRange(root, h.start, h.end, `lnat-pearson-hl lnat-pearson-hl-${h.color || 'yellow'}`, {
        backgroundColor: fill,
      });
    });
    (strikes || []).forEach((s) => {
      if (!paneMatches(s, pane, optionLabel)) return;
      wrapOffsetRange(root, s.start, s.end, 'lnat-pearson-strike', {
        textDecorationColor: '#333333',
      });
    });
  }, [highlights, strikes, pane, optionLabel, colorScheme, mathJaxEpoch, questionId]);

  return (
    <div
      ref={rootRef}
      className={className}
      data-lnat-annotate-pane={pane}
      data-lnat-option-label={pane === 'option' ? optionLabel : undefined}
      style={{ userSelect: 'text' }}
    >
      {children}
    </div>
  );
}
